'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import { DestinationLink } from '../blocks/DestinationLink'

import * as React from 'react'
import { NewsletterForm } from '../blocks/NewsletterForm'
import { ProductRail } from '../blocks/Rail'
import { House,
  Award,
  BellRing,
  BookOpen,
  ChartLine,
  Check,
  CircleCheck,
  Minimize2,
  Link2,
  Mail,
  MapPin,
  Palette,
  Refrigerator,
  Ruler,
  Search,
  ShieldCheck,
  Snowflake,
  Sparkles,
  TrendingUp,
  Beer,
} from '@/components/homepage-october/lucide'

import { Badge } from '@/components/homepage-october/ui/badge'
import { Button } from '@/components/homepage-october/ui/button'
import { Card } from '@/components/homepage-october/ui/card'
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/homepage-october/ui/carousel'
import { Field, FieldError, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/homepage-october/ui/input'
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/homepage-october/ui/input-group'
import { Item, ItemActions, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@/components/homepage-october/ui/item'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { keepKeyboardFocusInColumns } from '@/lib/keyboard-focus-in-view'
import { ArrowRight } from '../blocks/icons'
import { Img } from '../blocks/Img'
import { ChipLinks, Eyebrow, Section, SectionHeader, words } from '../blocks/layout'
import { MASCOT } from '../blocks/Frosty'
import { useDragScroll, useReducedMotion } from '../hooks'
import { useShop } from '../state'
import type { Model, SocialLink } from '../types'

/* ------------------------------------------------------------------ shared: tooltips, social icons, copy link */
type CopyInfo = { aria: string; url: string; tip: string }

/** Links the model marks external open in a new tab, as they do in v2. */
const ext = (l: { ext?: boolean }) => (l.ext ? ({ target: '_blank', rel: 'noopener' } as const) : {})

const ICON_LINK =
  'inline-flex size-10 items-center justify-center rounded-full border border-neutral-200 bg-white shadow-xs transition hover:-translate-y-0.5 hover:border-[#0469f0]/40 hover:shadow-md'
const SMALL_LABEL = 'text-xs font-semibold uppercase tracking-[0.12em] text-neutral-500'

/** shadcn Tooltip, drawn light (the stock one is near-black; the page uses light surfaces only). */
function Tip({ tip, children }: { tip?: string | null; children: React.ReactElement }) {
  if (!tip) return children
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent sideOffset={6} className="border border-neutral-200 bg-white! font-medium text-neutral-900! shadow-md [&_svg]:bg-white! [&_svg]:fill-white!">
        {tip}
      </TooltipContent>
    </Tooltip>
  )
}

/** A round share or follow icon link; the email link has no icon in the model, so it gets lucide Mail. */
function SocialIcon({ l }: { l: SocialLink }) {
  // The tooltip says what the link does in full ("Share Fridge.com on X"), the same words as its accessible name.
  return (
    <Tip tip={l.aria || l.tip}>
      <DestinationLink href={l.href} aria-label={l.aria} {...ext(l)} className={ICON_LINK}>
        {l.icon ? <img src={l.icon} alt="" className={compactClassName("size-5")} loading="lazy" /> : <Mail aria-hidden="true" className="size-5 text-neutral-700" />}
      </DestinationLink>
    </Tip>
  )
}

/** Copies the page link and says so in a toast; if the browser blocks the clipboard, the toast shows the link instead. */
function CopyLink({ c }: { c: CopyInfo }) {
  const { notify } = useShop()
  const copy = () => {
    const done = () => notify('Link copied', c.url, 'success')
    const fail = () => notify('This browser blocked copying', c.url)
    if (!navigator.clipboard?.writeText) {
      fail()
      return
    }
    navigator.clipboard.writeText(c.url).then(done, fail)
  }
  return (
    <Tip tip={c.tip}>
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={c.aria}
        onClick={copy}
        className="size-10! rounded-full! border-neutral-200! bg-white! shadow-xs hover:-translate-y-0.5 hover:border-[#0469f0]/40! hover:bg-white! hover:shadow-md"
      >
        <Link2 aria-hidden="true" className="size-5 text-neutral-700" />
      </Button>
    </Tip>
  )
}

/* ------------------------------------------------------------------ V22 social: pin wall, follow, share */
type Pin = Model['social']['pins'][number]

const PIN_SPEED = [46, 58, 50, 62]

function PinLink({ pin, decor }: { pin: Pin; decor?: boolean }) {
  return (
    <DestinationLink
      href={pin.href}
      target="_blank"
      rel="noopener"
      tabIndex={decor ? -1 : undefined}
      aria-hidden={decor || undefined}
      className="group/pin block overflow-hidden rounded-xl bg-white shadow-[0_12px_28px_-16px_rgba(16,24,40,0.45)] ring-1 ring-black/5 transition duration-300 hover:-translate-y-0.5 hover:shadow-lg"
    >
      <Img src={pin.img.src} alt={decor ? '' : pin.img.alt} fit="cover" className="aspect-[2/3] w-full transition duration-500 group-hover/pin:scale-[1.04]" />
    </DestinationLink>
  )
}

/**
 * Each column owns a different set of pins. Repeating within that column keeps the vertical loop filled
 * without putting the same pin in neighboring columns. Only the first appearance is an accessible link.
 */
function pinColumns(pins: Pin[], cols: number, per = 5) {
  const n = pins.length
  if (!n) return []
  const len = Math.max(per, Math.ceil(n / cols))
  return Array.from({ length: Math.min(cols, n) }, (_, c) => {
    const own = pins.filter((_, i) => i % cols === c)
    return Array.from({ length: len }, (_, j) => ({ pin: own[j % own.length], own: j < own.length }))
  })
}

/** md and up: columns of pins drifting up and down at different speeds. Hover or focus pauses the wall. */
function PinWall({ m }: { m: Model['social'] }) {
  const [api, setApi] = React.useState<CarouselApi>()
  const [paused, setPaused] = React.useState(false)
  const reduced = useReducedMotion()
  const cols = pinColumns(m.pins, Math.max(5, Math.ceil(m.pins.length / 2)))
  React.useEffect(() => {
    if (!api) return
    const stop = () => setPaused(true)
    api.on('pointerDown', stop)
    return () => { api.off('pointerDown', stop) }
  }, [api])
  // FRIDGE-1582 (FRIDGE-1508 K12): focus pauses the drift (homepage.css), but the focused pin stayed where the drift
  // had it: at 1280 with motion on, 9 of 10 pin stops sat above the clip box (production a86cb20e, 10/20/2026). Under
  // keyboard focus the pin's column is shifted so the pin sits inside the box's unfaded middle; when focus leaves the
  // wall the shift is cleared and the drift resumes. A tap or click moves nothing (lib/keyboard-focus-in-view.ts).
  React.useEffect(() => {
    if (!api) return
    return keepKeyboardFocusInColumns(api)
  }, [api])
  return (
    <Carousel
      setApi={setApi}
      opts={{ align: 'start', containScroll: 'trimSnaps', watchDrag: true, duration: reduced ? 0 : 20 }}
      aria-label={m.wallAria}
      tabIndex={0}
      data-pin-wall
      data-paused={paused || reduced}
      onKeyDown={(e) => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') setPaused(true) }}
      className="v3-marquee relative hidden h-[520px] overflow-hidden rounded-2xl border border-[#d8e6fb] bg-gradient-to-b from-[#fff7f0] via-white to-[#f3f8ff] shadow-[0_24px_60px_-34px_rgba(4,105,240,0.45)] md:block lg:h-auto lg:min-h-[600px]"
    >
      <div className={compactClassName("absolute inset-0 px-3 [mask-image:linear-gradient(to_bottom,transparent,#000_9%,#000_91%,transparent)] motion-reduce:overflow-y-auto")}>
        <CarouselContent className="-ml-3">
        {cols.map((col, c) => (
          <CarouselItem key={c} className="basis-1/3 pl-3 xl:basis-1/4">
            <div
              data-motion
              className={compactClassName(cn('v3-marquee-track flex animate-v3-marquee-up flex-col', c % 2 === 1 && '[animation-direction:reverse]'))}
              style={{ '--v3-marquee-duration': `${PIN_SPEED[c % PIN_SPEED.length]}s` } as React.CSSProperties}
            >
              <div className={compactClassName("flex flex-col gap-3 pb-3")}>
                {col.map((s, j) => <PinLink key={j} pin={s.pin} decor={!s.own} />)}
              </div>
              <div className={compactClassName("flex flex-col gap-3 pb-3 motion-reduce:hidden")} aria-hidden="true" inert>
                {col.map((s, j) => <PinLink key={j} pin={s.pin} decor />)}
              </div>
            </div>
          </CarouselItem>
        ))}
        </CarouselContent>
      </div>
    </Carousel>
  )
}

/** Phone: six pins as a still, three-column grid (every pin links to the same Pinterest profile, and the full
 *  wall is in the page for md and up), so the section stays short on a phone. */
function PinGrid({ m }: { m: Model['social'] }) {
  return (
    <div role="region" aria-label={m.wallAria} className={compactClassName("grid grid-cols-3 gap-2 md:hidden")}>
      {m.pins.slice(0, 6).map((p) => <PinLink key={p.img.src} pin={p} />)}
    </div>
  )
}

// Each network's own colour, as a light tint behind its icon.
const NET: Record<string, { tint: string; hover: string }> = {
  Pinterest: { tint: 'bg-[#fdecef]!', hover: 'hover:border-[#e60023]/35!' },
  Instagram: { tint: 'bg-[#fdeef7]!', hover: 'hover:border-[#d62976]/35!' },
  YouTube: { tint: 'bg-[#fdeeee]!', hover: 'hover:border-[#ff0000]/30!' },
  Google: { tint: 'bg-[#eef3fe]!', hover: 'hover:border-[#4285f4]/40!' },
}

function FollowCard({ f }: { f: Model['social']['follow'] }) {
  return (
    <Card className="gap-0! overflow-hidden py-0!">
      <div aria-hidden="true" className={compactClassName("h-1.5 bg-gradient-to-r from-[#0469f0] to-[#ff9951]")} />
      <div className={compactClassName("p-5 sm:p-6")}>
        <h3 className={compactClassName("text-xl font-semibold tracking-tight text-neutral-950")}>{f.title}</h3>
        <p className={compactClassName("mt-1.5 text-sm leading-relaxed text-neutral-600")}>{f.text}</p>
        <ul className={compactClassName("mt-4 grid gap-2")}>
          {f.buttons.map((b) => {
            const n = NET[b.name] ?? { tint: 'bg-neutral-100!', hover: '' }
            return (
              <li key={b.href}>
                <Item asChild variant="outline" size="sm" className={cn('flex-nowrap! gap-3! rounded-xl! bg-white px-3! transition! duration-200! hover:-translate-y-0.5 hover:bg-white! hover:shadow-md', n.hover)}>
                  <DestinationLink href={b.href} target="_blank" rel="noopener">
                    <ItemMedia className={cn('size-10! translate-y-0! self-center! rounded-full!', n.tint)}>
                      <img src={b.icon} alt="" className={compactClassName("size-5")} loading="lazy" />
                    </ItemMedia>
                    <ItemContent className="min-w-0 gap-0.5!">
                      <ItemTitle className="text-[15px]! font-semibold! text-neutral-950">{b.name}</ItemTitle>
                      <ItemDescription className="text-[13px]! text-neutral-600!">{b.handle}</ItemDescription>
                    </ItemContent>
                    <ItemActions>
                      <span className={compactClassName("inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-semibold text-neutral-800 transition group-hover/item:bg-[#0469f0] group-hover/item:text-white")}>
                        {words(b.go)}
                        <ArrowRight aria-hidden="true" className="size-3.5" />
                      </span>
                    </ItemActions>
                  </DestinationLink>
                </Item>
              </li>
            )
          })}
        </ul>
        <DestinationLink href={f.gnews.href} {...ext(f.gnews)} className="mt-4 inline-block rounded-lg transition hover:-translate-y-0.5">
          <img src={f.gnews.img.src} alt={f.gnews.img.alt} className={compactClassName("h-10 w-auto")} loading="lazy" />
        </DestinationLink>
      </div>
    </Card>
  )
}

function ShareCard({ s }: { s: Model['social']['share'] }) {
  const shop = useShop()
  const labelId = React.useId()
  return (
    <Card className="gap-0! overflow-hidden py-0!">
      <div aria-hidden="true" className={compactClassName("h-1.5 bg-gradient-to-r from-[#0469f0] via-[#0469f0] to-[#d62976]")} />
      <div className={compactClassName("p-5 sm:p-6")}>
        <h3 className={compactClassName("text-xl font-semibold tracking-tight text-neutral-950")}>{s.title}</h3>
        <p className={compactClassName("mt-1.5 text-sm leading-relaxed text-neutral-600")}>{s.text}</p>
        <p id={labelId} className={compactClassName(cn('mt-5', SMALL_LABEL))}>{s.label}</p>
        <ul aria-labelledby={labelId} className={compactClassName("mt-2.5 flex flex-wrap gap-2")}>
          {s.links.map((l) => (
            <li key={l.href}><SocialIcon l={l} /></li>
          ))}
          <li><CopyLink c={s.copy} /></li>
        </ul>
        {/* v2 drew Ask Frosty as a link to "#"; it opens the Frosty panel, so it is a button. */}
        <button
          type="button"
          aria-haspopup="dialog"
          onClick={(e) => shop.open('frosty', e.currentTarget)}
          className={compactClassName("mt-5 flex w-full items-center gap-3 rounded-xl border border-[#0469f0]/15 bg-gradient-to-r from-[#eef5ff] via-[#f6f9ff] to-white p-3 text-left transition hover:-translate-y-0.5 hover:border-[#0469f0]/35 hover:shadow-md")}
        >
          {/* The same mascot as the pill at 48 px, tilting its head on the same clock (owner 10/15/2026); drawn whole with
              nothing behind it (owner 10/18/2026: not a circle). */}
          <span className={compactClassName("flex size-12 shrink-0 items-center justify-center")}>
            <img src={MASCOT} alt={s.frosty.img.alt} width={48} height={48} data-motion="" className={compactClassName("v3-frosty-tilt size-12 object-contain")} loading="lazy" />
          </span>
          <span className={compactClassName("min-w-0 flex-1")}>
            <span className={compactClassName("flex items-center gap-1.5 text-[15px] font-semibold text-neutral-950")}>
              <Sparkles aria-hidden="true" className="size-4 text-[#0469f0]" />
              {s.frosty.title}
            </span>
            <span className={compactClassName("mt-0.5 block text-[13px] leading-snug text-neutral-600")}>{s.frosty.text}</span>
          </span>
          <ArrowRight aria-hidden="true" className="size-4 shrink-0 text-[#0454c0]" />
        </button>
        <DestinationLink
          href={s.preferred.href}
          {...ext(s.preferred)}
          className="mt-3 inline-flex items-center gap-2.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-[13px] font-medium text-neutral-900 shadow-xs transition hover:border-[#0469f0]/40 hover:text-[#0454c0]"
        >
          <img src={s.preferred.icon} alt="" className={compactClassName("size-5")} loading="lazy" />
          {s.preferred.text}
        </DestinationLink>
      </div>
    </Card>
  )
}

export function Social({ model }: { model: Model }) {
  const m = model.social
  return (
    <Section aria={m.aria} v2="V22" tone="lilac" className="overflow-hidden">
      <div aria-hidden="true" className={compactClassName("pointer-events-none absolute inset-0")}>
        <div className={compactClassName("absolute -top-32 -left-24 size-[460px] rounded-full bg-[radial-gradient(circle,rgba(230,0,35,0.09),transparent_65%)]")} />
        <div className={compactClassName("absolute -right-28 bottom-0 size-[540px] rounded-full bg-[radial-gradient(circle,rgba(4,105,240,0.12),transparent_65%)]")} />
      </div>
      <div className={compactClassName("relative")}>
        <SectionHeader head={m.head} tone="lilac" />
        <div className={compactClassName("grid gap-5 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:gap-6")}>
          <PinGrid m={m} />
          <PinWall m={m} />
          <div className={compactClassName("grid gap-5 md:grid-cols-2 lg:grid-cols-1")}>
            <FollowCard f={m.follow} />
            <ShareCard s={m.share} />
          </div>
        </div>
      </div>
    </Section>
  )
}

/* ------------------------------------------------------------------ V23 most searched: a directory of cards */
type Look = { Icon: typeof Search; chip: string; bar: string }
const GROUP_LOOKS: (Look & { test: RegExp })[] = [
  { test: /colou?r|finish/i, Icon: Palette, chip: 'bg-[#fff1e6] text-[#b54708]', bar: 'from-[#ff9951] to-[#ffcfae]' },
  { test: /guide|calculator|tool/i, Icon: BookOpen, chip: 'bg-[#eef5ff] text-[#0454c0]', bar: 'from-[#0469f0] to-[#9cc4fb]' },
  { test: /room|space/i, Icon: House, chip: 'bg-[#fff7f0] text-[#b54708]', bar: 'from-[#ff9951] to-[#ffcfae]' },
  { test: /brand/i, Icon: Award, chip: 'bg-[#eef2fb] text-[#004aad]', bar: 'from-[#004aad] to-[#6f9cf0]' },
  { test: /budget|size/i, Icon: Ruler, chip: 'bg-[#ecfdf5] text-[#067647]', bar: 'from-[#10a65c] to-[#86dcb0]' },
  { test: /keg|wine/i, Icon: Beer, chip: 'bg-[#eef5ff] text-[#0454c0]', bar: 'from-[#0469f0] to-[#9cc4fb]' },
  { test: /mini/i, Icon: Minimize2, chip: 'bg-[#fff1e6] text-[#b54708]', bar: 'from-[#ff9951] to-[#ffcfae]' },
  { test: /freez/i, Icon: Snowflake, chip: 'bg-[#e8f7fb] text-[#0e7490]', bar: 'from-[#0891b2] to-[#86d8ec]' },
  { test: /refrig|fridge/i, Icon: Refrigerator, chip: 'bg-[#eef5ff] text-[#0454c0]', bar: 'from-[#0469f0] to-[#8cbcff]' },
]
const GROUP_DEFAULT: Look = { Icon: TrendingUp, chip: 'bg-[#fdf2f8] text-[#be185d]', bar: 'from-[#db2777] to-[#f6b0d3]' }
const lookFor = (title: string) => GROUP_LOOKS.find((g) => g.test.test(title)) ?? GROUP_DEFAULT

// A long group with no counts (v2's "Also searched") is a chip cloud, set full width under the grouped lists.
const CLOUD_MIN = 13

function GroupHead({ title, look }: { title: string; look: Look }) {
  return (
    <>
      <div aria-hidden="true" className={compactClassName(cn('h-1 bg-gradient-to-r', look.bar))} />
      <div className={compactClassName("flex items-center gap-3 px-5 pt-5 pb-3")}>
        <span className={compactClassName(cn('flex size-9 shrink-0 items-center justify-center rounded-lg', look.chip))}>
          <look.Icon aria-hidden="true" className={compactClassName("size-[18px]")} />
        </span>
        <h3 className={compactClassName("text-lg font-semibold tracking-tight text-neutral-950")}>{title}</h3>
      </div>
    </>
  )
}

export function Searches({ model }: { model: Model }) {
  const m = model.searches
  const row = React.useRef<HTMLUListElement>(null)
  const drag = useDragScroll(row)
  const lists = m.groups.filter((g) => g.links.length < CLOUD_MIN)
  const clouds = m.groups.filter((g) => g.links.length >= CLOUD_MIN)
  return (
    <Section aria={m.aria} v2="V23" tone="sky">
      <SectionHeader head={m.head} tone="sky" />
      {/* Phone: the groups are one swipe row (every link stays in the page); from 640 px they are a grid. */}
      <ul ref={row} {...drag} className={compactClassName("v3-noscrollbar -mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 sm:pb-0 sm:[&>li:last-child]:col-span-2 lg:grid-cols-3 lg:[&>li:last-child]:col-span-1")}>
        {lists.map((g) => {
          const look = lookFor(g.title)
          return (
            <li key={g.title} className={compactClassName("w-[84%] shrink-0 snap-start sm:w-auto")}>
              <Card className="h-full gap-0! overflow-hidden py-0!">
                <GroupHead title={g.title} look={look} />
                <ul className={compactClassName("grid px-2.5 pb-3")}>
                  {g.links.map((l) => (
                    <li key={l.href + l.text}>
                      <DestinationLink href={l.href} className="group/link flex min-h-9 items-center justify-between gap-3 rounded-lg px-2.5 py-1.5 text-sm text-neutral-800 transition hover:bg-[#f3f8ff] hover:text-[#0454c0]">
                        <span className={compactClassName("min-w-0")}>{l.text}</span>
                        {l.count ? (
                          <Badge variant="secondary" className="tabular rounded-full! bg-neutral-100! px-2! font-semibold! text-neutral-600! group-hover/link:bg-white!">
                            {' '}{l.count}
                          </Badge>
                        ) : (
                          <ArrowRight aria-hidden="true" className="size-3.5 shrink-0 text-neutral-400 transition group-hover/link:translate-x-0.5 group-hover/link:text-[#0454c0]" />
                        )}
                      </DestinationLink>
                    </li>
                  ))}
                </ul>
              </Card>
            </li>
          )
        })}
      </ul>
      <ProductRail {...model.discoveryRails.outdoorFridges} className="v3-discovery-rail" />
      {clouds.map((g) => (
        <Card key={g.title} className="mt-4 gap-0! overflow-hidden py-0!">
          <GroupHead title={g.title} look={lookFor(g.title)} />
          <ChipLinks links={g.links} drift className="px-5 pt-1 pb-5" />
        </Card>
      ))}
    </Section>
  )
}

/* ------------------------------------------------------------------ V24 about */
// v2's card icons, in order: search, shield, chart, award.
const ABOUT_LOOKS = [
  { Icon: Search, chip: 'bg-[#eef5ff] text-[#0454c0]' },
  { Icon: ShieldCheck, chip: 'bg-[#ecfdf5] text-[#067647]' },
  { Icon: ChartLine, chip: 'bg-[#eef5ff] text-[#0454c0]' },
  { Icon: Award, chip: 'bg-[#fff1e6] text-[#b54708]' },
]

export function About({ model }: { model: Model }) {
  const m = model.about
  return (
    <Section aria={m.aria} v2="V24" tone="mist">
      <SectionHeader head={m.head} tone="mist" />
      <ul className={compactClassName("grid gap-4 sm:grid-cols-2 lg:grid-cols-4")}>
        {m.cards.map((c, k) => {
          const L = ABOUT_LOOKS[k % ABOUT_LOOKS.length]
          return (
            <li key={c.title}>
              <Card className="h-full gap-0! py-0!">
                <div className={compactClassName("p-4 sm:p-5")}>
                  <span className={compactClassName(cn('flex size-9 items-center justify-center rounded-lg', L.chip))}>
                    <L.Icon aria-hidden="true" className="size-5" />
                  </span>
                  <h3 className={compactClassName("mt-3 text-lg font-semibold tracking-tight text-neutral-950")}>{c.title}</h3>
                  <p className={compactClassName("mt-1.5 text-sm leading-relaxed text-neutral-600")}>{c.text}</p>
                </div>
              </Card>
            </li>
          )
        })}
      </ul>
      <ChipLinks links={m.links} className="mt-6" />
    </Section>
  )
}

/* ------------------------------------------------------------------ V24b the promise */
export function Promise({ model }: { model: Model }) {
  const m = model.promise
  const id = React.useId()
  const labelId = `${id}-label`
  const inputId = `${id}-zip`
  return (
    <Section aria={m.aria} v2="V24b" tone="mint">
      <Card className="relative gap-0! overflow-hidden py-0! shadow-[0_30px_70px_-40px_rgba(6,118,71,0.45)]! lg:grid! lg:grid-cols-[1.05fr_0.95fr]">
        <div className={compactClassName("p-6 sm:p-10 lg:p-12")}>
          <Eyebrow tone="mint">{m.eyebrow}</Eyebrow>
          <h2 className={compactClassName("mt-3 text-balance text-3xl font-bold tracking-tight text-neutral-950 md:text-[2.5rem] md:leading-[1.1]")}>{m.title}</h2>
          <ul className={compactClassName("mt-6 grid gap-3.5")}>
            {m.lines.map((l) => (
              <li key={l} className={compactClassName("flex items-start gap-3 text-base leading-relaxed text-neutral-700")}>
                <span className={compactClassName("mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-[#ecfdf5] text-[#067647] ring-1 ring-[#067647]/15")}>
                  <Check aria-hidden="true" strokeWidth={3} className="size-3.5" />
                </span>
                {l}
              </li>
            ))}
          </ul>
          <form action={m.zip.action} method={m.zip.method} className={compactClassName("mt-8 max-w-md")}>
            <p id={labelId} className={compactClassName("mb-2 flex items-center gap-1.5 text-sm font-semibold text-neutral-900")}>
              <MapPin aria-hidden="true" className="size-4 text-[#067647]" />
              {m.zip.title}
            </p>
            <InputGroup className="h-14! rounded-full! border-neutral-300! bg-white! pr-1.5 pl-2 shadow-[0_8px_30px_-14px_rgba(0,0,0,0.3)]! has-[[data-slot=input-group-control]:focus-visible]:border-[#0469f0]!">
              {/* The model's aria-label stays; aria-labelledby adds the visible label so the name carries both. */}
              <InputGroupInput
                id={inputId}
                name={m.zip.name}
                aria-label={m.zip.aria}
                aria-labelledby={`${labelId} ${inputId}`}
                placeholder={m.zip.placeholder}
                inputMode="numeric"
                autoComplete="postal-code"
                maxLength={5}
                pattern="[0-9]{5}"
                required
                className="text-base! md:text-base!"
              />
              <InputGroupAddon align="inline-end">
                <InputGroupButton type="submit" variant="ctaPrimary" className="h-11! gap-1.5! rounded-full! px-5! text-[15px]! font-semibold!">
                  {words(m.zip.button)}
                  <ArrowRight aria-hidden="true" className="size-4" />
                </InputGroupButton>
              </InputGroupAddon>
            </InputGroup>
          </form>
          <p className={compactClassName("mt-8 flex size-32 -rotate-6 items-center justify-center rounded-full border-2 border-dashed border-[#0469f0]/35 bg-white p-4 text-center text-[13px] font-bold leading-tight text-[#004aad] shadow-[0_16px_40px_-18px_rgba(4,74,173,0.55)] ring-4 ring-white lg:absolute lg:right-8 lg:bottom-8 lg:z-10 lg:mt-0")}>
            {m.seal}
          </p>
        </div>
        <div className={compactClassName("relative aspect-[4/3] lg:aspect-auto lg:min-h-[520px]")}>
          <Img src={m.img.src} alt={m.img.alt} fit="cover" className="absolute inset-0 size-full" />
        </div>
      </Card>
    </Section>
  )
}

/* ------------------------------------------------------------------ V25 price and recall alerts */
function AlertsForm({ f }: { f: Model['alerts']['form']; error: string }) { return <NewsletterForm names source="homepage-alerts" button={f.button} /> }

export function Alerts({ model }: { model: Model }) {
  const m = model.alerts
  return (
    <Section id={m.id} aria={m.aria} v2="V25" tone="white">
      <Card className="relative gap-0! overflow-hidden border-[#0469f0]/15! bg-gradient-to-br from-[#e6f0ff] via-[#f4f8ff] to-white py-0! shadow-[0_30px_70px_-40px_rgba(4,105,240,0.45)]!">
        <div aria-hidden="true" className={compactClassName("pointer-events-none absolute inset-0")}>
          <div className={compactClassName("absolute -top-24 -left-20 size-[360px] rounded-full bg-[radial-gradient(circle,rgba(4,105,240,0.14),transparent_65%)]")} />
          <div className={compactClassName("absolute -right-24 -bottom-28 size-[380px] rounded-full bg-[radial-gradient(circle,rgba(255,153,81,0.16),transparent_65%)]")} />
        </div>
        <div className={compactClassName("relative grid gap-6 p-5 sm:p-8 lg:grid-cols-2 lg:items-center lg:gap-10 lg:p-10")}>
          <div>
            <Badge variant="outline" className="gap-1.5! rounded-full! border-[#ff9951]/45! bg-white! px-3! py-1! font-semibold! uppercase tracking-[0.1em] text-[#b54708]! [&>svg]:size-3.5!">
              <BellRing aria-hidden="true" data-motion="" className="v3-bell" />
              {m.pill}
            </Badge>
            <h2 className={compactClassName("mt-4 text-balance text-3xl font-bold tracking-tight text-neutral-950 md:text-[2.5rem] md:leading-[1.1]")}>{m.title}</h2>
            <p className={compactClassName("mt-3 max-w-md text-pretty text-base leading-relaxed text-neutral-600 md:text-lg")}>{m.text}</p>
          </div>
          {/* The alerts slice has no error line of its own; it reuses the deals alert's wording. */}
          <AlertsForm f={m.form} error={model.deals.alert.error} />
        </div>
      </Card>
    </Section>
  )
}

export { Footer } from '../blocks/SiteFooter'
